import { doc, getFirestore, runTransaction, setDoc } from 'firebase/firestore';
import moment from 'moment';

const COUNTERS_COLLECTION = 'order-counters';

export type BillSequenceType = 'FD' | 'RM';

export interface OrderSequenceResult {
    orderSeqNo: string;
    orderSeqNum: number;
    fiscalYear: string;
}

/** Indian FY (Apr–Mar): e.g. May 2026 → "2026-27", Feb 2027 → "2026-27". */
export function getIndianFiscalYearKey(date?: number | Date): string {
    const m = moment.utc(date ?? Date.now());
    const month = m.month();
    const year = m.year();

    if (month >= 3) {
        return `${year}-${String((year + 1) % 100).padStart(2, '0')}`;
    }
    return `${year - 1}-${String(year % 100).padStart(2, '0')}`;
}

export function formatOrderSeqNo(
    fiscalYearKey: string,
    billType: BillSequenceType,
    seq: number
): string {
    return `FY${fiscalYearKey}-${billType}-${String(seq).padStart(4, '0')}`;
}

function getCounterDocId(restaurantId: string, fiscalYearKey: string, billType: BillSequenceType): string {
    return `${restaurantId}__${fiscalYearKey}__${billType}`;
}

/**
 * Atomically reserves the next bill sequence number for a restaurant + FY + type.
 * Safe for concurrent checkouts via Firestore transaction retries.
 */
export async function getNextOrderSeqNo(
    restaurantId: string,
    billType: BillSequenceType,
    billDate?: number
): Promise<OrderSequenceResult> {
    if (!restaurantId) {
        throw new Error('restaurantId is required to generate orderSeqNo');
    }

    const db = getFirestore();
    const fiscalYear = getIndianFiscalYearKey(billDate);
    const counterRef = doc(db, COUNTERS_COLLECTION, getCounterDocId(restaurantId, fiscalYear, billType));

    return runTransaction(db, async (transaction) => {
        const counterSnap = await transaction.get(counterRef);
        const lastSeq = counterSnap.exists() ? Number(counterSnap.data()?.lastSeq || 0) : 0;
        const nextSeq = lastSeq + 1;

        transaction.set(
            counterRef,
            {
                lastSeq: nextSeq,
                fiscalYear,
                billType,
                restaurantId,
                updatedAt: moment.utc().valueOf(),
            },
            { merge: true }
        );

        return {
            orderSeqNo: formatOrderSeqNo(fiscalYear, billType, nextSeq),
            orderSeqNum: nextSeq,
            fiscalYear,
        };
    });
}

/** Sets counter to a known lastSeq after backfill so new bills continue correctly. */
export async function syncOrderCounter(
    restaurantId: string,
    fiscalYearKey: string,
    billType: BillSequenceType,
    lastSeq: number
): Promise<void> {
    if (!restaurantId || lastSeq < 1) {
        return;
    }

    const db = getFirestore();
    const counterRef = doc(db, COUNTERS_COLLECTION, getCounterDocId(restaurantId, fiscalYearKey, billType));

    await setDoc(
        counterRef,
        {
            lastSeq,
            fiscalYear: fiscalYearKey,
            billType,
            restaurantId,
            updatedAt: moment.utc().valueOf(),
        },
        { merge: true }
    );
}
